/**
 * Simple directives.
 *
 * File: \src\directives.ts
 * Created Date: Friday, February 8th 2019, 14:39:54
 * Author: Sascha Herrmann
 * -----
 * Last Modified: July 23rd 2024, 10:09:20
 * Modified By: Boryana Kircheva (HAU-ED)
 * -----
 * Copyright (c) 2017-2019 Liebherr-Hausgeräte GmbH
 */
import Vue from 'vue';

/**
 * Shorttext
 *
 * Used as "v-shorttext" on nodes containing text. It adds the formatting needed
 * to shorten a text to the given amount of pixels.
 *
 * Usage:
 *
 * <div ... v-shorttext='160'>Some text here</div>
 *
 * or:
 *
 * <span .... v-shorttext='140'>{{ someAttributeValue }}</span>
 *
 * You can also use it without a fixed value, then the directive tries to access clientWidth
 * of the element and use that.
 *
 * <div v-shorttext>{{ getApplianceName(appliances) }}</div>
 *
 * It adds the required formatting to the element to make sure that the text is
 * shortened and ellipsis are added.
 */
import { createApp } from 'vue';
import App from './App.vue';

const app = createApp(App);

app.directive('shorttext', {
  mounted: (el, binding) => {
    if (binding?.value) {
      el.style.maxWidth = binding.value + 'px';
    } else {
      el.style.maxWidth = el.clientWidth + 'px';
    }

    el.style.textOverflow = 'ellipsis';
    el.style.overflow = 'hidden';
    el.style.display = 'block';
    el.style.whiteSpace = 'nowrap';
  }
});

/**
 * Resizer (v-resizer)
 *
 * Used as "v-resizer" on HTML nodes like DIVs. Notifies you with the new width and height of the
 * browser window (!) and of the element.
 * You pass the callback that should be invoked on window resizes and you can make it fire immediately.
 *
 * Callback:
 *
 * (windowWidth: number, windowHeight: number, elementWidth: number, elementHeight: number) => void
 *
 * Example:
 *
 * <div ... v-resizer:immediate='onWindowResize'></div>
 *
 * Then, in that component, you can write a:
 *
 * public onWindowResize(windowWidth, windowHeight, elementWidth, elementHeight) {...}
 *
 * which will be invoked on resize events. Since "immediate" is passed as arg, it fires immediately when creating the directive.
 * You can also use it without "immediate".
 */
app.directive('resizer', {
  mounted: (el, binding) => {
    const callback: (windowWidth: number, windowHeight: number, elementWidth: number, elementHeight: number) => void =
      binding.value;

    if (el['resizer']) {
      return;
    }

    // Will be invoked on resizes
    const resizeFunction = () => {
      const windowWidth: number = window.innerWidth;
      const windowHeight: number = window.innerHeight;
      const elementWidth: number = el.clientWidth;
      const elementHeight: number = el.clientHeight;

      callback(windowWidth, windowHeight, elementWidth, elementHeight);
    };

    const observer = new ResizeObserver(resizeFunction);
    observer.observe(el);
    el['resizer'] = observer;

    if (binding.arg === 'immediate') {
      resizeFunction();
    }
  },

  unmounted: el => {
    if (el['resizer']) {
      const observer: ResizeObserver = el['resizer'] as ResizeObserver;
      observer.unobserve(el);
      el['resizer'] = undefined;
    }
  }
});
